import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { IDemoSignUpParams } from '@aqx-core/common/api/user-account';
import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { useSignUpDemoDefaultDeposit } from '@aqx-core/common/services/sign-up-demo';
import { signUpDemoViewController } from '@aqx-core/common/services/sign-up-demo/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';
import { EMAIL_REGEX, isValidPhoneNumber, validateName } from '@aqx-core/common/validators';

export interface ISignUpDemoValues extends Omit<IDemoSignUpParams, 'phoneNumber'> {
  dialCode: string;
  localPhoneNumber: string;
  agree: boolean;
}

export const useSignUpDemoForm = ({ defaultValues }: { defaultValues?: Partial<ISignUpDemoValues> } = {}) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .required(() => t('Name is required.'))
          .test(
            'is-valid-name',
            () => t('Please enter a valid name.'),
            (value) => !!value && validateName(value)
          ),
        email: yup
          .string()
          .required(() => t('Email is required.'))
          .matches(EMAIL_REGEX, () => t('Please enter a valid email.')),
        dialCode: yup.string().required(() => t('Dial code is required.')),
        localPhoneNumber: yup
          .string()
          .required(() => t('Phone number is required.'))
          .test(
            'is-valid-phone',
            () => t('Please enter a valid phone number.'),
            function (value) {
              const { dialCode } = this.parent;
              if (!dialCode || !value) {
                return false;
              }
              return isValidPhoneNumber(dialCode, value);
            }
          ),
        depositAmount: yup.number().required(() => t('Deposit is required.')),
        agree: yup
          .boolean()
          .required(() => t('Please review and accept the Terms and Conditions.'))
          .oneOf([true], () => t('Please review and accept the Terms and Conditions.'))
      }),
    [t]
  );

  const resolver = useMemo(() => yupResolver(schema), [schema]);
  const useFormMethods = useBaseForm<ISignUpDemoValues>({
    resolver,
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false
  });
  const defaultDeposit = useSignUpDemoDefaultDeposit();
  const { handleSubmit, trigger, watch, formState, setValue } = useFormMethods;

  const [name, email, dialCode, localPhoneNumber, depositAmount] = watch([
    'name',
    'email',
    'dialCode',
    'localPhoneNumber',
    'depositAmount'
  ]);

  const {
    errors: {
      name: nameError,
      email: emailError,
      dialCode: dialCodeError,
      localPhoneNumber: localPhoneNumberError,
      depositAmount: depositAmountError
    }
  } = formState;

  const isDisabled = useMemo(() => {
    return !!(
      !name ||
      !email ||
      !dialCode ||
      !localPhoneNumber ||
      !depositAmount ||
      nameError ||
      emailError ||
      dialCodeError ||
      localPhoneNumberError ||
      depositAmountError
    );
  }, [
    depositAmount,
    depositAmountError,
    dialCode,
    dialCodeError,
    email,
    emailError,
    localPhoneNumber,
    localPhoneNumberError,
    name,
    nameError
  ]);

  const onError = useCallback((message: string) => {
    snackbarController.showAlert({
      message,
      messageType: EUISnackBarMessageType.ERROR
    });
  }, []);

  const handleFormSubmit: SubmitHandler<ISignUpDemoValues> = useCallback(
    (values) => {
      const { dialCode, localPhoneNumber, ...rest } = values;
      const phoneNumber = `${dialCode} ${localPhoneNumber}`;

      signUpDemoViewController.openDemoAccount({ params: { phoneNumber, ...rest }, onError });
    },
    [onError]
  );

  const submit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      handleSubmit(handleFormSubmit)(e);
    },
    [handleSubmit, handleFormSubmit]
  );

  useEffect(() => {
    if (defaultDeposit) {
      setValue('depositAmount', defaultDeposit);
    }
  }, [defaultDeposit, setValue]);

  useEffect(() => {
    if (dialCode && localPhoneNumber) {
      trigger('localPhoneNumber');
    }
  }, [dialCode, localPhoneNumber, trigger]);

  return {
    useFormMethods,
    submit,
    trigger,
    isDisabled,
    name,
    email,
    dialCode,
    localPhoneNumber,
    depositAmount
  };
};
